import { NgClass } from "@angular/common";
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Chart, ChartDataset, TimeScaleOptions, TimeUnit } from "chart.js";
import { DateTime, DateTimeUnit } from "luxon";
import { FloatLabelModule } from "@openng/optimus-ui/floatlabel";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { TimeoutComponent } from "../../../../../shared/components/timeout/timeout.component";
import { createTimeoutSignal } from "../../../../../shared/utils/timeout.utils";
import { ChartChangeTimeEvent } from "../../../../../models/ui/chartData";
import "chartjs-adapter-luxon";
import { DataPoint, LineChartData } from "../../../../../models/ui/energyToolCharts";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { first } from "rxjs";
import { DatePickerModule, DatePickerTypeView } from "@openng/optimus-ui/datepicker";
import { TIMEFRAME_OPTIONS } from "../../../../../constants/timeFrameOptions";
import { DialogService } from "@openng/optimus-ui/dynamicdialog";
import { EnergyDataExportDialogComponent } from "../../energy-data-export-dialog/energy-data-export-dialog.component";
import { SvgIconComponent } from "../../../../../shared/components/svg-icon/svg-icon.component";
import { PermissionService } from "../../../../../services/api/permission/permission.service";
import { HasPermissionDirective } from "../../../../../shared/directives/has-permission/has-permission.directive";

@Component({
  standalone: true,
  selector: "app-line-chart",
  imports: [
    DatePickerModule,
    TranslateModule,
    FloatLabelModule,
    FormsModule,
    NgClass,
    ProgressSpinnerModule,
    TimeoutComponent,
    SvgIconComponent,
    TooltipModule,
    HasPermissionDirective,
  ],
  templateUrl: "./line-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./line-chart.component.scss",
})
export class LineChartComponent implements OnChanges, OnDestroy, AfterViewInit {
  public sourceId = input<string>();
  public sourceType = input<string>();
  public chartData = input<LineChartData[] | null>();
  public chartId = input<string>();

  @Output() changeTimeframeEvent = new EventEmitter<ChartChangeTimeEvent>();

  private lineChart!: Chart<"line", DataPoint[]>;
  public date: Date = new Date();

  public calendarView: DatePickerTypeView = "month";
  public dateFormat = "mm.yy";
  public currentFilterAggregation: DateTimeUnit = "month";
  private translate = inject(TranslateService);
  private dialogService = inject(DialogService);
  public permissions = inject(PermissionService);
  public filterAggregationOptions: DropdownOption[] = TIMEFRAME_OPTIONS;

  public loading = computed(() => {
    return this.chartData() === null;
  });
  public timeOut = createTimeoutSignal(this.loading);

  constructor() {
    effect(() => {
      const datasets = this.chartData();
      if (datasets && this.lineChart) {
        const lineDataSets: ChartDataset<"line", DataPoint[]>[] = [];
        datasets.forEach((dataset) => {
          if (dataset.data) {
            const lineSet: ChartDataset<"line", DataPoint[]> = {
              ...dataset,
              data: dataset.data,
            };
            lineDataSets.push(lineSet);
          }
        });
        this.lineChart.data.datasets = lineDataSets;
        this.setXAxisOptions();
        this.lineChart.update();
        this.setLabels(this.translate.currentLang);
      }
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: () => {
        this.setLabels(this.translate.currentLang);
      },
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      const sourceIdChanged = changes["sourceId"] && !changes["sourceId"].firstChange;
      const sourceTypeChanged = changes["sourceType"] && !changes["sourceType"].firstChange;
      if (sourceIdChanged || sourceTypeChanged) {
        this.currentFilterAggregation = "month";
        this.dateFormat = "mm.yy";
        this.date = new Date();
      }
    }
  }
  public ngOnDestroy() {
    if (this.lineChart) {
      this.lineChart.destroy();
    }
  }
  public ngAfterViewInit() {
    const chartId = this.chartId();
    if (!chartId) {
      return;
    }
    this.lineChart = new Chart(chartId, {
      type: "line",
      data: {
        datasets: [],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              color: "#ffffff",
            },
          },
        },
        elements: {
          line: {
            tension: 0.7,
          },
          point: {
            radius: 0,
            hoverRadius: 8,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "#727272",
            },
            ticks: {
              color: "#ffffff",
            },
          },
          x: {
            adapters: {
              date: {
                locale: this.translate.currentLang,
              },
            },
            type: "timeseries",
            time: {
              unit: "day",
              tooltipFormat: "dd. MMM",
              displayFormats: {
                hour: "HH:mm",
                day: "d",
                month: "MMM",
              },
            },
            grid: {
              color: "#727272",
            },
            ticks: {
              color: "#ffffff",
            },
          },
        },
      },
    });
  }

  /**
   * Opens the energy data export dialog for the current line chart asset.
   */
  protected openExportDialog(): void {
    const assetId = this.sourceId();
    const assetType = this.sourceType();
    if (!assetId || !assetType) {
      return;
    }

    this.dialogService.open<EnergyDataExportDialogComponent>(EnergyDataExportDialogComponent, {
      header: this.translate.instant("energy_tool.energy_data_export.dialog_header"),
      modal: true,
      focusOnShow: false,
      width: "700px",
      contentStyle: { overflow: "auto" },
      data: {
        assetId,
        assetType,
      },
      closable: true,
    });
  }

  /**
   * Translates the labels for the line chart data sets based on the given locale
   * and updates the locale of the x-axis.
   * @param lang The locale to use for the translation.
   */
  private setLabels(lang: string) {
    this.translate
      .get(["energy_tool.line_chart.energy_loss", "energy_tool.line_chart.normal_energy_loss"])
      .pipe(first())
      .subscribe({
        next: (translations) => {
          if (this.lineChart.data.datasets.length > 0) {
            this.lineChart.data.datasets[0].label = translations["energy_tool.line_chart.energy_loss"];
            this.lineChart.data.datasets[1].label = translations["energy_tool.line_chart.normal_energy_loss"];
            this.updateTimeseriesLocale(this.lineChart, "x", lang);
            this.lineChart.update();
          }
        },
      });
  }

  /**
   * Handles the selection of an aggregation option to update the chart view.
   * Adjusts the calendar view and date format based on the selected option,
   * and updates the time window accordingly.
   *
   * @param option - The selected aggregation option ("day", "week", "month", or "year").
   */

  public clickAggregationOption(option: string | number | boolean) {
    this.currentFilterAggregation = option as DateTimeUnit;
    switch (option) {
      case "day":
        this.calendarView = "date";
        this.dateFormat = "dd.mm.yy";
        break;
      case "week":
        // To display KW (week of year) set date Format to the formated value (via luxon) and on dateChange, prev and next window . so everytime date changes
        this.calendarView = "date";
        this.dateFormat = "dd.mm.yy";
        break;
      case "month":
        this.calendarView = "month";
        this.dateFormat = "mm.yy";
        break;
      case "year":
        this.calendarView = "year";
        this.dateFormat = "yy";
        break;
    }
    this.date = new Date(this.date);
    this.setTimeWindow();
  }

  /**
   * Updates the locale of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param locale The locale to use for the axis.
   */
  private updateTimeseriesLocale(chart: Chart<"line", DataPoint[]>, axis: "x" | "y", locale: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.adapters = {
        date: { locale },
      };
    }
  }

  /**
   * Updates the unit of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param unit The unit to use for the axis.
   */
  private updateTimeseriesUnit(chart: Chart<"line", DataPoint[]>, axis: "x" | "y", unit: TimeUnit) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.time.unit = unit;
    }
  }

  /**
   * Updates the tooltip format of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param format The format to use for the tooltip.
   */
  private updateTimeseriesTooltipFormat(chart: Chart<"line", DataPoint[]>, axis: "x" | "y", format: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.time.tooltipFormat = format;
    }
  }

  /**
   * Updates the display format of a timeseries axis on a chart.
   * @param chart The chart to update.
   * @param axis The axis to update.
   * @param format The format to use for the display.
   */
  private updateTimeseriesDisplayFormat(chart: Chart<"line", DataPoint[]>, axis: "x" | "y", format: string) {
    const scale = chart?.options?.scales?.[axis];
    if (scale?.type === "timeseries") {
      const ts = scale as TimeScaleOptions;
      ts.time.displayFormats["day"] = format;
    }
  }

  /**
   * Configures the x-axis of the bar chart based on the current filter aggregation.
   * - For "day": Sets time unit to "hour" and tooltip format to "HH:mm".
   * - For "week": Sets time unit to "day", display format to "EEE dd.MM.", and tooltip format to "dd.MM.yyyy".
   * - For "month": Sets time unit to "day", display format to "d", and tooltip format to "dd. MMM".
   * - For "year": Sets time unit to "month" and tooltip format to "MMM yyyy".
   */
  private setXAxisOptions() {
    switch (this.currentFilterAggregation) {
      case "day":
        // this.lineChart.options.scales.x.time.unit = "hour";
        this.updateTimeseriesUnit(this.lineChart, "x", "hour");
        this.updateTimeseriesTooltipFormat(this.lineChart, "x", "HH:mm");
        // this.lineChart.options.scales.x.time.tooltipFormat = "HH:mm";
        break;
      case "week":
        this.updateTimeseriesUnit(this.lineChart, "x", "day");
        this.updateTimeseriesDisplayFormat(this.lineChart, "x", "EEE dd.MM.");
        this.updateTimeseriesTooltipFormat(this.lineChart, "x", "dd.MM.yyyy");
        break;
      case "month":
        this.updateTimeseriesUnit(this.lineChart, "x", "day");
        this.updateTimeseriesDisplayFormat(this.lineChart, "x", "d");
        this.updateTimeseriesTooltipFormat(this.lineChart, "x", "dd. MMM");
        break;
      case "year":
        this.updateTimeseriesUnit(this.lineChart, "x", "month");
        this.updateTimeseriesTooltipFormat(this.lineChart, "x", "MMM yyyy");
        break;
    }
  }

  /**
   * Move the time window backward by one unit of the current filter aggregation.
   * Then calls setTimeWindow() to update the time window.
   */
  public previousTimeWindow() {
    this.date = DateTime.fromMillis(this.date.getTime())
      .minus({
        [this.currentFilterAggregation]: 1,
      })
      .toJSDate();

    this.setTimeWindow();
  }

  /**
   * Move the time window forward by one unit of the current filter aggregation.
   * Then calls setTimeWindow() to update the time window.
   */
  public nextTimeWindow() {
    this.date = DateTime.fromMillis(this.date.getTime())
      .plus({
        [this.currentFilterAggregation]: 1,
      })
      .toJSDate();

    this.setTimeWindow();
  }

  /**
   * Sets the time window by calculating the start and end timestamps based on the current date
   * and the current filter aggregation. Emits a changeTimeframeEvent with the calculated start,
   * end, and timeframe.
   */
  public setTimeWindow() {
    const start = DateTime.fromMillis(this.date.getTime())
      .startOf(this.currentFilterAggregation as DateTimeUnit)
      .valueOf();
    const end = DateTime.fromMillis(this.date.getTime())
      .endOf(this.currentFilterAggregation as DateTimeUnit)
      .valueOf();
    this.changeTimeframeEvent.emit({
      start: start,
      end: end,
      timeFrame: this.currentFilterAggregation,
    });
  }
}
